import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Tab } from "semantic-ui-react";

import { AttachmentsPreview, CUSTOM_CONFIRM_TYPES, CustomConfirm, DATE_FORMATS_FNS, FileDragDrop } from "components";
import "modules/AppointmentDetails/components/Interventions/components/Attachments/Attachments.scss";
import {
  useAddInterventionAttachmentMutation,
  useDeleteInterventionAttachmentMutation,
  useUploadInterventionAttachmentMutation
} from "modules/AppointmentDetails/components/Interventions/hooks";
import { useToggleAttachmentVisibilityMutation } from "modules/AppointmentDetails/hooks";
import { getAttachmentIcon } from "modules/AppointmentDetails/utils";
import { downloadFile, getNameFromURL } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { DiagnoseOverviewAttachment, Intervention, InterventionAttachment } from "models";

type AttachmentsProps = {
  intervention: Intervention;
  attachments?: (InterventionAttachment | DiagnoseOverviewAttachment)[];
};

export const Attachments = ({ intervention, attachments }: AttachmentsProps) => {
  const { t } = useTranslation();
  const [selectedDeleteAttachmentID, setSelectedDeleteAttachmentID] = useState<number>(0);
  const { mutateAsync: uploadAttachment } = useUploadInterventionAttachmentMutation();
  const { mutate: addInterventionAttachment } = useAddInterventionAttachmentMutation();
  const { mutate: deleteInterventionAttachment, isPending: isDeleteInterventionAttachmentPending } = useDeleteInterventionAttachmentMutation();
  const { mutate: toggleAttachmentVisibility } = useToggleAttachmentVisibilityMutation();

  const handleUploadAttachment = async (file: File) => {
    const extension = file.name.lastIndexOf(".") > -1 ? file.name.slice(file.name.lastIndexOf(".") + 1) : "unknown";
    const uploadFile = new File([file], file.name, { type: file.type || extension });

    const attachmentUrl = await uploadAttachment({ appointment_id: intervention.appointment_id, file: uploadFile });

    if (attachmentUrl) {
      addInterventionAttachment({
        url: attachmentUrl,
        intervention_id: intervention.id,
        type: file.type,
        name: getNameFromURL(attachmentUrl),
        visible_to_customer: true
      });
    }
  };

  const handleFileUpload = (files: File[]) => {
    files.forEach(file => handleUploadAttachment(file));
  };

  const onDeleteAttachment = (intervention_attachment_id: number) => {
    setSelectedDeleteAttachmentID(intervention_attachment_id);
  };

  const handleCancelDeleteAttachment = () => {
    setSelectedDeleteAttachmentID(0);
  };

  const handleConfirmDeleteAttachment = () => {
    if (selectedDeleteAttachmentID) {
      deleteInterventionAttachment({ intervention_attachment_id: selectedDeleteAttachmentID, intervention_id: intervention.id });
    }

    setSelectedDeleteAttachmentID(0);
  };

  const handleToggleAttachmentVisibility = (visible_to_customer: boolean, intervention_attachment_id?: number) => {
    toggleAttachmentVisibility({ intervention_attachment_id, visible_to_customer });
  };

  const handleOnAttachmentDownloadClick = async (url: string) => {
    await downloadFile(url);
  };

  const renderListView = () => {
    return (
      !!attachments?.length && (
        <div className="attachment-container">
          {attachments.map(attachment => {
            const attachmentUser = "user" in attachment ? attachment.user : undefined;
            const uploadedBy = attachmentUser ? `${attachmentUser.first_name} ${attachmentUser.last_name}`.trim() : "";
            const uploadedOn = attachment.created_on ? formatDate(attachment.created_on, DATE_FORMATS_FNS.dateMonthYearTime) : "";

            return (
              <div key={attachment.id} className="attachment-view">
                <div className="attachment-name" onClick={() => handleOnAttachmentDownloadClick(attachment.url)}>
                  <Icon className={getAttachmentIcon(attachment.url)} />
                  <p className="ellipsis">{attachment.name || (attachment.created_on && formatDate(attachment.created_on, DATE_FORMATS_FNS.dateMonthYear))}</p>
                </div>
                <div className="attachment-delete">
                  {attachment.created_on && (
                    <p>
                      {uploadedBy
                        ? `${t("v8_uploaded_by", "Uploaded by")} ${uploadedBy} ${t("v8_on", "on")} ${uploadedOn}`
                        : `${t("v8_uploaded_on", "Uploaded on")} ${uploadedOn}`}
                    </p>
                  )}
                  {attachment.id && attachment.name && <Icon className="trash grey" onClick={() => onDeleteAttachment(Number(attachment.id))} />}
                </div>
              </div>
            );
          })}
        </div>
      )
    );
  };

  const renderGalleryView = () => {
    return (
      !!attachments?.length && (
        <div className="attachment-container">
          <AttachmentsPreview
            attachments={attachments}
            onDeleteRequest={item => item.id && onDeleteAttachment(item.id)}
            onToggleVisibility={handleToggleAttachmentVisibility}
            canDelete={true}
          />
        </div>
      )
    );
  };

  return (
    <div className="Attachments">
      <Tab
        style={{ padding: "0" }}
        menu={{ attached: false, tabular: false }}
        panes={[
          {
            menuItem: { key: "gallery", icon: "photo film", content: t("v8_gallery", "Gallery") },
            render: () => (
              <>
                <FileDragDrop id="intervention-image" onFileUpload={handleFileUpload} />
                <Tab.Pane>{renderGalleryView()}</Tab.Pane>
              </>
            )
          },
          {
            menuItem: { key: "list", icon: "list", content: t("v8_list", "List") },
            render: () => (
              <>
                <FileDragDrop id="intervention-image" onFileUpload={handleFileUpload} />
                <Tab.Pane>{renderListView()}</Tab.Pane>
              </>
            )
          }
        ]}
        grid={{ paneWidth: 16, tabWidth: 8 }}
      />

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        handleCancel={handleCancelDeleteAttachment}
        isOpen={!!selectedDeleteAttachmentID}
        isLoading={isDeleteInterventionAttachmentPending}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleConfirmDeleteAttachment}
      />
    </div>
  );
};
